import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format, addDays } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { parseDateOnly } from '@/lib/dateOnly';
import { useCalendarDragDropStore } from '@/stores/calendarDragDropStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCalendarActionsStore } from '@/stores/calendarActionsStore';
import { useCalendarModalStore } from '@/stores/calendarModalStore';
import type { CalendarListEntry } from '@/types/calendar';

function formatTimeFromMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(mins).padStart(2, '0')} ${period}`;
}

function buildDateTime(date: string, minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${date}T${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

function getPopoverStyle(anchor: { x: number; y: number } | null) {
  if (typeof window === 'undefined' || !anchor) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const popoverWidth = 320;
  const popoverHeight = 200;
  const padding = 16;

  // Determine position - prefer below and to the right of click point
  let top = anchor.y + 8;
  let left = anchor.x;

  // Adjust if would go off right edge
  if (left + popoverWidth > window.innerWidth - padding) {
    left = window.innerWidth - popoverWidth - padding;
  }
  // Adjust if would go off left edge
  if (left < padding) {
    left = padding;
  }

  // Adjust if would go off bottom
  if (top + popoverHeight > window.innerHeight - padding) {
    top = anchor.y - popoverHeight - 8;
  }
  // Adjust if would go off top
  if (top < padding) {
    top = padding;
  }

  return { top, left };
}

export function CalendarQuickCreate() {
  const quickCreate = useCalendarDragDropStore((s) => s.quickCreate);
  const closeQuickCreate = useCalendarDragDropStore((s) => s.closeQuickCreate);
  const createEvent = useCalendarActionsStore((s) => s.createEvent);
  const creatingEvent = useCalendarActionsStore((s) => s.creatingEvent);
  const calendars = useCalendarStore((s) => s.calendars);
  const calendarOrder = useCalendarStore((s) => s.calendarOrder);
  const timezone = useCalendarStore((s) => s.timezone);
  const defaultCreateCalendarUid = useCalendarStore((s) => s.defaultCreateCalendarUid);
  const openCreateDialogWithDraft = useCalendarModalStore((s) => s.openCreateDialogWithDraft);

  const [title, setTitle] = useState('');
  const [calendarUid, setCalendarUid] = useState<string | null>(null);
  const [allDay, setAllDay] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Get writable calendars
  const calendarOptions = useMemo(() => {
    return calendarOrder
      .map((uid) => calendars[uid])
      .filter((cal): cal is CalendarListEntry => cal != null && cal.canEdit === true);
  }, [calendarOrder, calendars]);

  // Reset state when popover opens
  useEffect(() => {
    if (quickCreate.open) {
      setTitle('');
      setError(null);
      // Set allDay based on whether time is provided
      setAllDay(quickCreate.startMinutes === null);
      // Select default calendar or first available
      const defaultCalendar = defaultCreateCalendarUid && calendarOptions.some(c => c.uid === defaultCreateCalendarUid)
        ? defaultCreateCalendarUid
        : calendarOptions[0]?.uid ?? null;
      setCalendarUid(defaultCalendar);
      // Focus input after a short delay to allow render
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [quickCreate.open, quickCreate.startMinutes, calendarOptions, defaultCreateCalendarUid]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && quickCreate.open) {
        closeQuickCreate();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [quickCreate.open, closeQuickCreate]);

  const handleSubmit = useCallback(async () => {
    if (!calendarUid) {
      setError('Select a calendar');
      return;
    }

    const effectiveTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const eventTitle = title.trim() || 'Untitled event';

    let startValue: { date: string | null; dateTime: string | null; timeZone: string | null };
    let endValue: { date: string | null; dateTime: string | null; timeZone: string | null };

    if (allDay || quickCreate.startMinutes === null) {
      // All-day event
      const endDate = format(addDays(parseDateOnly(quickCreate.date)!, 1), 'yyyy-MM-dd');
      startValue = { date: quickCreate.date, dateTime: null, timeZone: null };
      endValue = { date: endDate, dateTime: null, timeZone: null };
    } else {
      // Timed event
      const startDateTime = buildDateTime(quickCreate.date, quickCreate.startMinutes);
      const endMinutes = quickCreate.endMinutes ?? quickCreate.startMinutes + 60;
      const endDateTime = buildDateTime(quickCreate.date, endMinutes);
      startValue = { date: null, dateTime: startDateTime, timeZone: effectiveTimezone };
      endValue = { date: null, dateTime: endDateTime, timeZone: effectiveTimezone };
    }

    const result = await createEvent({
      calendarUid,
      event: {
        summary: eventTitle,
        start: startValue,
        end: endValue,
        allDay: allDay || quickCreate.startMinutes === null,
      },
      sendUpdates: 'none',
    });

    if (result) {
      closeQuickCreate();
    } else {
      setError('Failed to create event');
    }
  }, [calendarUid, title, allDay, quickCreate, timezone, createEvent, closeQuickCreate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleMoreOptions = useCallback(() => {
    // Open full modal with pre-filled data
    openCreateDialogWithDraft(calendarUid, {
      summary: title.trim() || undefined,
      date: quickCreate.date,
      startMinutes: allDay ? null : quickCreate.startMinutes,
      endMinutes: allDay ? null : quickCreate.endMinutes,
      allDay: allDay || quickCreate.startMinutes === null,
    });
    closeQuickCreate();
  }, [calendarUid, title, quickCreate, allDay, openCreateDialogWithDraft, closeQuickCreate]);

  if (!quickCreate.open) {
    return null;
  }

  const displayDate = format(parseDateOnly(quickCreate.date)!, 'EEE, MMM d');
  const displayTime = quickCreate.startMinutes !== null && !allDay
    ? `${formatTimeFromMinutes(quickCreate.startMinutes)} – ${formatTimeFromMinutes(quickCreate.endMinutes ?? quickCreate.startMinutes + 60)}`
    : 'All day';

  const containerStyle = getPopoverStyle(quickCreate.anchorPosition);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={closeQuickCreate}
      />
      {/* Popover */}
      <div
        className="fixed z-50 w-80 rounded-xl border border-border/60 bg-background shadow-xl"
        style={containerStyle}
      >
        <div className="p-4 space-y-3">
          {/* Title input */}
          <Input
            ref={inputRef}
            placeholder="Add title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            className="text-base font-medium"
            autoFocus
          />

          {/* Date and time display */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{displayDate}</span>
            <span className="text-muted-foreground">{displayTime}</span>
          </div>

          {/* All-day toggle (only show if started as timed event) */}
          {quickCreate.startMinutes !== null && (
            <div className="flex items-center justify-between">
              <Label htmlFor="all-day-toggle" className="text-sm text-muted-foreground">
                All day
              </Label>
              <Switch
                id="all-day-toggle"
                checked={allDay}
                onCheckedChange={setAllDay}
              />
            </div>
          )}

          {/* Calendar selector */}
          <Select value={calendarUid ?? ''} onValueChange={setCalendarUid}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select calendar" />
            </SelectTrigger>
            <SelectContent>
              {calendarOptions.map((calendar) => (
                <SelectItem key={calendar.uid} value={calendar.uid}>
                  <div className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full shrink-0"
                      style={{ backgroundColor: calendar.backgroundColor ?? '#2563eb' }}
                    />
                    <span className="truncate">{calendar.summary}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Error message */}
          {error && (
            <p className="text-xs text-destructive">{error}</p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={handleMoreOptions}
              className="text-sm text-primary hover:underline"
            >
              More options
            </button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={creatingEvent || !calendarUid}
            >
              {creatingEvent ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Add'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
